import { TableRow, TableCell } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { handleOverviewSecurityClick } from './SecurityOverviewHeader'
import { getSecurityAsync, getSecurityFromCache, getSecurityKey } from '../../util/security'
import OrderBookDialog from '../orderBook/OrderBookDialog'
import IndexComponentSymbolCell from './indexComponentSymbolCell'
import IndexComponentPriceCells from './indexComponentPriceCells'
import IndexComponentRowActions from './indexComponentRowActions'
import { Security } from '../../proto/generated/security'
import { indexComponentRowCellDividerSx } from './indexComponentTableStyles'

type Props = {
    component: IndexComponentData
    subscribeSecurity: (security: Security, subscribe: boolean) => void
    showContribution?: boolean
}

export type IndexComponentData = {
    id: string
    security: Security
    ltp: number | null
    changePercentage: number | null
    contribution: number | null
    weightage: number | null
    previousClose: number
    spi: number
}

const ComponentView = ({ component, subscribeSecurity, showContribution = true }: Props) => {
    const { security: securityProp, previousClose, spi, ltp, changePercentage, contribution } = component
    const secKey = useMemo(() => getSecurityKey(securityProp.exchange, securityProp.code), [securityProp])
    const [security, setSecurity] = useState<Security | undefined>(() => getSecurityFromCache(secKey))
    const [expanded, setExpanded] = useState(false)
    const expandedRef = useRef(expanded)
    expandedRef.current = expanded

    useEffect(() => {
        if (!secKey) return
        const cached = getSecurityFromCache(secKey)
        if (cached) {
            setSecurity(cached)
            return
        }
        getSecurityAsync(secKey).then(setSecurity)
    }, [secKey])

    const handleComponentClick = useCallback(() => {
        if (security) handleOverviewSecurityClick(security)
    }, [security])

    const handleToggleExpand = useCallback(() => {
        if (!security) return
        const next = !expandedRef.current
        subscribeSecurity(security, next)
        setExpanded(next)
    }, [subscribeSecurity, security])

    if (!security) {
        return null
    }

    return (
        <>
            <TableRow
                hover
                onClick={handleComponentClick}
                sx={{
                    '&:hover .action-btns': { visibility: 'visible' },
                    height: 36,
                    position: 'relative',
                    ...indexComponentRowCellDividerSx,
                }}
            >
                <IndexComponentSymbolCell security={security} />
                <IndexComponentPriceCells
                    securityKey={secKey}
                    security={security}
                    previousClose={previousClose}
                    spi={spi}
                    initialLtp={ltp}
                    initialChangePercentage={changePercentage}
                    initialContribution={contribution}
                    showContribution={showContribution}
                    ltpCellSuffix={
                        <IndexComponentRowActions security={security} onToggleExpand={handleToggleExpand} />
                    }
                />
            </TableRow>

            {expanded && (
                <TableRow>
                    <TableCell colSpan={showContribution ? 4 : 3}>
                        <OrderBookDialog security={security} />
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

function componentViewPropsEqual(prev: Props, next: Props): boolean {
    return (
        prev.component.id === next.component.id &&
        prev.component.previousClose === next.component.previousClose &&
        prev.component.spi === next.component.spi &&
        prev.component.security?.code === next.component.security?.code &&
        prev.subscribeSecurity === next.subscribeSecurity &&
        prev.showContribution === next.showContribution
    )
}

export default memo(ComponentView, componentViewPropsEqual)
